import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { investingApi } from "../../services/api";
import { scoreboardSeries } from "./lib/scoreboard";

/**
 * At most this many pages (100 rows each) of a list are read for the line.
 * Snapshots past it: the line draws the newest it read and says so.
 * Contributions or distributions past it: no line — a shadow missing one
 * would be wrong, not shorter.
 */
export const SERIES_PAGE_CAP = 20;

/**
 * Every page of a list, up to the cap: `{rows, count, complete}`. A row
 * that shows up on two pages (the list shifted while it was read) is kept
 * once, by id.
 */
async function everyPage(list) {
  const rows = new Map();
  let page = 1;
  for (;;) {
    const { data } = await list({ page });
    for (const row of data?.results ?? []) rows.set(row.id, row);
    if (!data?.next) return { rows: [...rows.values()], count: data?.count ?? rows.size, complete: true };
    if (page >= SERIES_PAGE_CAP) return { rows: [...rows.values()], count: data.count, complete: false };
    page += 1;
  }
}

const EMPTY = { loading: true, error: null, snapshots: null, contributions: null, distributions: null };

/**
 * The scoreboard line's records — GET snapshots/, contributions/ and
 * benchmark-distributions/, every page — and the series lib/scoreboard
 * works out from them: `{loading, error, series, incomplete, truncated,
 * snapshotsShown, snapshotsTotal, retry}`. `series` is null when it
 * failed, when the contributions or distributions run past the cap
 * (`incomplete`), or when a row can't be read. Reloads when `reloadTick`
 * moves; only the newest read lands.
 */
export default function useScoreboardSeries(reloadTick = 0) {
  const [state, setState] = useState(EMPTY);
  const [retryTick, setRetryTick] = useState(0);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    setState((now) => ({ ...now, loading: true, error: null }));
    Promise.all([
      everyPage(investingApi.snapshots.list),
      everyPage(investingApi.contributions.list),
      everyPage(investingApi.distributions.list),
    ]).then(
      ([snapshots, contributions, distributions]) => {
        if (request !== requestRef.current) return;
        setState({ loading: false, error: null, snapshots, contributions, distributions });
      },
      (err) => {
        if (request !== requestRef.current) return;
        setState({ ...EMPTY, loading: false, error: err });
      },
    );
  }, [reloadTick, retryTick]);

  const retry = useCallback(() => setRetryTick((tick) => tick + 1), []);

  const { snapshots, contributions, distributions } = state;
  const incomplete = Boolean(contributions && distributions && !(contributions.complete && distributions.complete));
  const series = useMemo(() => {
    if (!snapshots || !contributions || !distributions || incomplete) return null;
    return scoreboardSeries(snapshots.rows, contributions.rows, distributions.rows);
  }, [snapshots, contributions, distributions, incomplete]);

  return {
    loading: state.loading,
    error: state.error,
    series,
    incomplete,
    truncated: Boolean(snapshots && !snapshots.complete),
    snapshotsShown: snapshots?.rows.length ?? 0,
    snapshotsTotal: snapshots?.count ?? 0,
    retry,
  };
}
